import { AccountPanel, SIGNED_IN } from "./account";
import { PatronPanel } from "./patron";
import { PACES, readPace, savePace } from "./pace";
import { PACKS, PLANS, type PackId, type Plan } from "../shared/patron";
import { api, ApiError } from "./api";
import { PLAYER_VOICES, VOICE_BLURBS, VOICE_NAMES } from "../shared/voices";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { DrawAsChoice, Resource } from "../shared/story";

/**
 * Trust and first run, shared by every theme (docs/BETA.md 3–5): the 18+ gate with a live example, the privacy page,
 * the feedback and delete-everything sheets, and the meta-commands typed into the Line. Themes only place the links.
 */

export { metaOf, type Meta } from "./meta";
import type { Meta } from "./meta";
import { FROM_FEEDBACK } from "./changes";

/** 16+ (owner decision 2026-10-16); the key changed from the 13+ gate so earlier taps ask again. */
/** 18+ at launch (owner, 2026-10-17); the key changed from the beta's 16+ tap, so everyone is asked again. */
const AGE_KEY = "loom:age18";
export function ageOk(): boolean { try { return localStorage.getItem(AGE_KEY) === "1"; } catch { return false; } }
export function setAgeOk(): void { try { localStorage.setItem(AGE_KEY, "1"); } catch { /* the tap still counts for this visit */ } }

/** Who makes Weftwake, at the foot of the gate, privacy, the album, onboarding and every theme's home. */
export function Credit({ className = "" }: { className?: string }) {
  return (
    <p className={`lm-credit ${className}`}>
      Weftwake is developed by InterfaceDelta, Inc., a small, boutique software development firm in Philadelphia.
      Questions: <a href="mailto:hello@interfacedelta.com">hello@interfacedelta.com</a>
    </p>
  );
}

/** The privacy and feedback links every theme shows (story footer and home). */
export function TrustLinks({ meta }: { meta(m: Meta): void }) {
  return (
    <>
      <a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>privacy</a>
      <a href="/account" onClick={(e) => { e.preventDefault(); meta("account"); }}>account settings</a>
      <a href="/safety" onClick={(e) => { e.preventDefault(); meta("safety"); }}>safety</a>
      <a href="/terms">terms</a>
      <button type="button" onClick={() => meta("feedback")}>feedback</button>
      <button type="button" onClick={() => meta("tour")}>how to play</button>
    </>
  );
}

// ---- first run: a live example plays itself; one tap; nothing is stored or fetched before it ---------------------

const DEMOS = [
  {
    you: "The ferry left without me, but my suitcase is on it.",
    loom: "The landing stage is wet and empty, and the ferry is already a low shape on the grey water, its wake closing behind it like a seam. Your hand feels wrong without the handle. On the bench by the harbour office an old man mends a net that runs off his knees and down the planks, further than it should. Somewhere past the boat sheds, someone is whistling a tune with a gap in it.",
  },
  {
    you: "I follow the whistling.",
    loom: "The shed door is heavier than it looks. Inside, the lights are blue and the benches are empty, except for a ferryman's cap, damp, set neatly on a coil of rope. The whistling stops the moment you step in. Then, from behind you, it starts again.",
  },
];

function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(() => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const q = matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(q.matches);
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, []);
  return reduce;
}

/** Attract mode: a player's line types itself, then the narrator writes, word by word. Loops; still under reduced motion. */
function Attract() {
  const reduce = useReducedMotion();
  const [step, setStep] = useState({ demo: 0, you: 0, loom: 0 });
  useEffect(() => {
    if (reduce) return;
    const d = DEMOS[step.demo]!;
    const words = d.loom.split(" ");
    let next: typeof step, wait: number;
    if (step.you < d.you.length) { next = { ...step, you: step.you + 1 }; wait = 34; }
    else if (step.loom < words.length) { next = { ...step, loom: step.loom + 1 }; wait = step.loom === 0 ? 700 : 75; }
    else { next = { demo: (step.demo + 1) % DEMOS.length, you: 0, loom: 0 }; wait = 4200; }
    const t = window.setTimeout(() => setStep(next), wait);
    return () => window.clearTimeout(t);
  }, [step, reduce]);
  const d = DEMOS[reduce ? 0 : step.demo]!;
  const you = reduce ? d.you : d.you.slice(0, step.you);
  const loom = reduce ? d.loom : d.loom.split(" ").slice(0, step.loom).join(" ");
  return (
    <figure className="lm-attract" aria-label="An example of playing Weftwake">
      <p className="lm-attract-you"><span aria-hidden="true">›</span> {you}{!reduce && step.you < d.you.length && <span className="lm-caret" aria-hidden="true" />}</p>
      <p className="lm-attract-loom">{loom}{!reduce && step.you >= d.you.length && step.loom < d.loom.split(" ").length && <span className="lm-caret" aria-hidden="true" />}</p>
      <figcaption>An example. Your story starts with whatever line you write.</figcaption>
    </figure>
  );
}

export function Gate({ onOk, meta }: { onOk(): void; meta(m: Meta): void }) {
  return (
    <main className="lm-gate">
      {/* The knot (public/brand, tools/knot.py); its crossing is cut in the night ground's colour, the gate's in both schemes. */}
      <img className="lm-gate-mark" src="/brand/knot-night.svg" width="72" height="72" alt="" />
      <h1>Weftwake</h1>
      <p className="lm-gate-what">A text adventure that writes itself as you play. You write what you do, a line or a page; the world grows from what you write, and every night it dreams about your story.</p>
      <Attract />
      <button type="button" className="lm-cta" onClick={onOk}>I'm 18 or older</button>
      <p className="lm-gate-small">For adults (18+). No account needed. Your stories are private to you. <a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>How Weftwake handles your stories</a></p>
      <Credit />
    </main>
  );
}

// ---- privacy -----------------------------------------------------------------------------------------

export function Privacy({ onBack, meta }: { onBack(): void; meta(m: Meta): void }) {
  return (
    <main className="lm-page lm-privacy">
      <a href="/" onClick={(e) => { e.preventDefault(); onBack(); }}>← back</a>
      <h1>Privacy</h1>
      <p className="lm-privacy-lede">You can play without an account: a random id in a cookie tells your stories apart from everyone else's. Sign in only if you want your stories on every device. Here is what Weftwake keeps, where, for how long and who sees it. Your sign-in, Patronage and settings are in <a href="/account" onClick={(e) => { e.preventDefault(); meta("account"); }}>Account settings</a>; what happens if a story gets too close is on the <a href="/safety" onClick={(e) => { e.preventDefault(); meta("safety"); }}>Safety</a> page.</p>
      <div className="lm-privacy-grid">
        <section className="lm-privacy-info" aria-label="What Weftwake keeps">
          <div className="lm-privacy-block">
          <h2>What Weftwake keeps</h2>
          <ul>
            <li>Your stories: every line you write and every reply, the world they build (places, people, things, open threads), the recaps and the "look closer" texts.</li>
            <li>Chapter images (plates) the nightly dream draws, with their captions and codes.</li>
            <li>Your time zone, so the dream runs during your night.</li>
            <li>Each day, a count of first visits and first stories for each place visitors came from: the referring site's name (like "theresanaiforthat.com") or a link's tag (like "itch"). Only the counts are kept, never linked to you or your stories.</li>
            <li>For one day, a count of free turns per network, under a salted one-way hash of your IP address (so free turns can't be multiplied by clearing cookies). Never linked to your stories.</li>
            <li>If you sign in: your email address, and when you last signed in. A sign-in code is kept only as a salted hash, for ten minutes.</li>
            <li>Your settings: whether new stories have chapters, whether you turned on mature themes, when you confirmed you are 18 or older, and, if you chose one, whether plates draw you as a man or a woman (it only shapes those pictures).</li>
            <li>On this device only: your theme, your reading pace, which dreams you've seen, and that you tapped "I'm 18 or older".</li>
          </ul>
          </div>
          <div className="lm-privacy-block">
          <h2>Where</h2>
          <ul>
            <li>Your stories live in a private store on Cloudflare that only your id opens. Images are in private Cloudflare storage and are served only to you.</li>
            <li>A small bookkeeping table holds story ids, turn counts, your time zone and when a story was last dreamt. No story text.</li>
            <li>Feedback goes to an anonymous table: only what you type in the feedback box, which screen you were on, and the app version. No id, no story. If you fill in "Email me if you'd like a reply", that address is kept with the note, used only to answer it, and isn't linked to your stories or account.</li>
          </ul>
          </div>
          <div className="lm-privacy-block">
          <h2>For how long</h2>
          <p>Until you delete it. Delete one story from its page, or everything with "Delete everything". Nothing about you is kept after that, apart from feedback you sent (with the reply address, if you gave one).</p>
          </div>
          <div className="lm-privacy-block">
          <h2>Who sees it</h2>
          <p>Only you, unless you pass an opening on: then whoever has the link sees its first line, the truths you set and the narrator (never the rest of your story or your fear), and you each see the other's chapter cards. A reported opening is hidden at once and reviewed within 24 hours. A plate you save is yours to share: the file holds the picture, its caption, the plate number and its code, never your story. Plates are keepsakes: Weftwake doesn't sell plates or run a market for them.</p>
          </div>
          <div className="lm-privacy-block">
          <h2>Which AI reads it</h2>
          <ul>
            <li><strong>Anthropic's Claude</strong> writes the replies (Claude Sonnet 5.5) and keeps the world, writes "look closer", the nightly dream and the safety check (Claude Haiku 5.5). Requests pass through Cloudflare's AI Gateway, which records counts like tokens and cost but no copy of what you write or what is written back. Under Anthropic's commercial terms, this data is not used to train their models.</li>
            <li><strong>Jev, by TypeSafe</strong>, reads what kind of move you made and how tense the moment is. It receives only a short summary of the scene, your latest line, the short labels of up to three open threads and the chapter's question (and, at a chapter's crisis, a short list of what you've secured), never the story, under a zero-data-retention agreement.</li>
            <li><strong>Cloudflare Workers AI</strong> draws the chapter images from a short description of the scene that the dream writes.</li>
            <li>A safety check reads your lines for signs that you, not your character, may be in danger. If so, Weftwake pauses and shows where to get help.</li>
          </ul>
          </div>
          <div className="lm-privacy-block">
          <h2>Also</h2>
          <ul>
            <li>The fonts load from Google Fonts, so Google sees that your browser asked for them.</li>
            <li>Weftwake is for adults, 18 and older.</li>
            <li>Chapter images are AI-generated, and a saved plate says so on the mount and inside the file.</li>
          </ul>
          </div>
        </section>
        <aside className="lm-privacy-controls" aria-label="Your data">
          <h2>Your data</h2>
          <DataControls meta={meta} />
          <h2>Who makes Weftwake</h2>
          <Credit />
        </aside>
      </div>
    </main>
  );
}

function DataControls({ meta }: { meta(m: Meta): void }) {
  return (
    <>
      <div className="lm-privacy-actions">
        <button type="button" onClick={() => meta("export")}>Export my data</button>
        <button type="button" className="lm-danger" onClick={() => meta("delete")}>Delete everything</button>
      </div>
      <p className="lm-gate-small">You can also type "export my data", "delete everything", "account", "privacy", "feedback" or "help" into the line at any time.</p>
    </>
  );
}

// ---- safety ---------------------------------------------------------------------------------------------

/** The published crisis protocol (owner's compliance list): what Weftwake notices, what it does, what it keeps. */
export function Safety({ onBack, meta }: { onBack(): void; meta(m: Meta): void }) {
  return (
    <main className="lm-page lm-privacy lm-safety">
      <a href="/" onClick={(e) => { e.preventDefault(); onBack(); }}>← back</a>
      <h1>Safety</h1>
      <p className="lm-privacy-lede">Stories in Weftwake can be dark. Danger inside a story is fiction, and the narrator follows your lead. But sometimes a line is no longer about the character. This is what Weftwake does then.</p>
      <div className="lm-privacy-block">
        <h2>If you are in danger now</h2>
        <ul>
          <li>In the US, call or text <strong>988</strong> (Suicide &amp; Crisis Lifeline), any time, free and confidential, or chat at 988lifeline.org.</li>
          <li>Text <strong>HOME</strong> to <strong>741741</strong> to reach the Crisis Text Line.</li>
          <li>Call <strong>911</strong> (or your local emergency number) if you or someone else is in immediate danger.</li>
          <li>Outside the US: <a href="https://findahelpline.com" rel="noopener">findahelpline.com</a> lists free, confidential helplines by country.</li>
        </ul>
        <p>Weftwake is a story game. It is not a crisis service and not therapy.</p>
      </div>
      <div className="lm-privacy-block">
        <h2>What Weftwake notices</h2>
        <p>Every line you write is checked before the story goes on: first against a short list of explicit phrases, then by an AI safety check (Anthropic's Claude Haiku), which is told that danger inside the story is fiction and that a person stepping outside it to speak about their real life is not. No person reads your lines.</p>
      </div>
      <div className="lm-privacy-block">
        <h2>What happens then</h2>
        <ul>
          <li>The story pauses and shows the ways to reach someone above. Nothing in your story is lost.</li>
          <li>It stays paused until you say you're okay.</li>
          <li>After that, for seven days, only the explicit-phrase check runs, so a dark story isn't interrupted again for being dark. Explicit phrases still pause it.</li>
          <li>If the safety check can't run, Weftwake errs on the side of care and shows the resources.</li>
          <li>The narrator never encourages anyone to hurt themselves or others, and steers away from real-world instructions for harm.</li>
        </ul>
      </div>
      <div className="lm-privacy-block">
        <h2>What is kept</h2>
        <p>That the safety screen was shown, in your own private store (which story, and whether a phrase or the AI check paused it), so the story knows it is paused. No one at Weftwake is notified, and Weftwake never contacts anyone on your behalf. <a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>How Weftwake handles your stories</a>.</p>
      </div>
      <div className="lm-privacy-block">
        <h2>Questions or a concern</h2>
        <p>Write to <a href="mailto:hello@interfacedelta.com">hello@interfacedelta.com</a>.</p>
      </div>
      <Credit />
    </main>
  );
}

// ---- account settings ----------------------------------------------------------------------------------

/** Account settings (owner, 2026-10-17: settings shouldn't hide under "privacy"): who you are, Patron, how stories play. */
/** Why a test letter didn't go, in the player's words (the route says which; the email service's own reason if it refused). */
function testLetterFailed(e: unknown): string {
  const body = (e instanceof ApiError ? e.body : null) as { error?: string; detail?: string } | null;
  if (body?.error === "no_story") return "Couldn't send: play a turn in a story first.";
  if (body?.error === "not_subscribed") return "Couldn't send: this account isn't signed up for morning letters.";
  if (body?.error === "send_failed") return `Couldn't send: the email service said "${body.detail ?? "no reason given"}".`;
  return `Couldn't send (${e instanceof ApiError ? e.status : "no connection"}). Try again in a moment.`;
}

export function AccountSettings({ onBack, meta, back = "stories" }: { onBack(): void; meta(m: Meta): void; back?: "story" | "stories" }) {
  return (
    <main className="lm-page lm-privacy lm-account-page">
      <a className="lm-acct-back" href="/" onClick={(e) => { e.preventDefault(); onBack(); }}>{back === "story" ? "← Back to your story" : "← Back to your stories"}</a>
      <header className="lm-acct-head">
        <h1>Account settings</h1>
        <p>Your account, how your stories are told, and your data. Every change saves as you make it.</p>
      </header>
      <div className="lm-account-grid">
        <div className="lm-account-col">
          <p className="lm-acct-group">You</p>
          <Card title="Your account"><AccountPanel /></Card>
          {/* The Patron panel names itself ("Become a Patron" or "Your Patronage"). */}
          <Card><PatronPanel reason={null} embedded /></Card>
          <Card title="Morning letters"><LettersSetting /></Card>
        </div>
        <div className="lm-account-col">
          <p className="lm-acct-group">Your stories</p>
          <Card title="Narrator"><VoiceSetting /></Card>
          <Card title="Reading speed"><PaceSetting /></Card>
          <Card title="Plates: draw me as"><DrawAsSetting /></Card>
          <Card title="Chapters"><ChaptersSwitch /></Card>
          <Card title="Mature themes (18+)"><MatureSwitch /></Card>
          <Card title="Tell us what you think">
            <p>Weftwake is young and grows often: new voices, new ways to play, better stories. Problems, praise, ideas: every note is read by a person.</p>
            <div className="lm-privacy-actions"><button type="button" onClick={() => meta("feedback")}>Send feedback</button></div>
          </Card>
        </div>
        <div className="lm-account-col lm-account-wide">
          <p className="lm-acct-group">Purchases and data</p>
          <div className="lm-account-pair">
            <Card title="Withdraw from a purchase"><WithdrawSetting /></Card>
            <Card title="Your data">
              <DataControls meta={meta} />
              <p className="lm-gate-small"><a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>How Weftwake handles your stories</a></p>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
}

/** One setting on the account page: a card that takes its look from the current theme. */
function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="lm-card">
      {title && <h2>{title}</h2>}
      {children}
    </section>
  );
}

/**
 * Morning letters: an email after the loom dreams one of your stories (opt-in; needs an account, since it needs an
 * address). What it holds, how often, and how to stop it, said before the switch.
 */
function LettersSetting() {
  const [s, setS] = useState<{ on: boolean; signedIn: boolean; available: boolean; test?: boolean } | null>(null);
  const [tested, setTested] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.letters().then(setS, () => setFailed(true)); }, []);
  useEffect(() => {
    const on = () => { api.letters().then(setS, () => setFailed(true)); };
    window.addEventListener(SIGNED_IN, on);
    return () => window.removeEventListener(SIGNED_IN, on);
  }, []);
  const set = (on: boolean) => {
    setBusy(true); setFailed(false);
    api.setLetters(on).then((r) => setS((x) => (x ? { ...x, on: r.on } : x)), () => setFailed(true)).finally(() => setBusy(false));
  };
  return (
    <div className="lm-mature">
      <p>A letter in the morning after the loom dreams one of your stories: what it dreamt, the new plate, what has begun to stir, and the way back in. At most three a week, only for stories you played in the last two weeks. Every letter has a one-click way to stop them.</p>
      {!s ? <p className="lm-gate-small">{failed ? "Couldn't load this setting." : "…"}</p>
        : !s.signedIn ? <p className="lm-gate-small">Sign in above first: letters need an email address.</p>
        : !s.available ? <p className="lm-gate-small">Morning letters open soon.</p>
        : (
          <div className="lm-privacy-actions">
            {s.on
              ? <button type="button" disabled={busy} onClick={() => set(false)}>Stop morning letters</button>
              : <button type="button" disabled={busy} onClick={() => set(true)}>Send me morning letters</button>}
            <span className="lm-gate-small lm-state" data-on={s.on ? "1" : "0"}>{s.on ? "On" : "Off"}</span>
            {s.on && s.test && (
              <button type="button" disabled={busy} onClick={() => { setBusy(true); api.testLetter().then(() => setTested("Sent. Check your inbox (and spam), and try replying to it."), (e) => setTested(testLetterFailed(e))).finally(() => setBusy(false)); }}>Send me a test letter now (beta)</button>
            )}
          </div>
        )}
      {tested && <p className="lm-gate-small" role="status">{tested}</p>}
      {failed && s && <p role="alert">Couldn't save that. Try again in a moment.</p>}
    </div>
  );
}

export const moneyOf = (cents: number, cur: string) => { try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur.toUpperCase() }).format(cents / 100); } catch { return `${(cents / 100).toFixed(2)} ${cur}`; } };
export const purchaseName = (item: string) => (item in PLANS ? PLANS[item as Plan].label : item in PACKS ? `Turn pack, ${PACKS[item as PackId].turns} turns` : item);
const DAY_MS = 86_400_000;
/** How a withdrawal's refund stands, in words both sides read the same way. */
export const refundStatus = (w: { withdrawn_at: number; refund_due: number; refunded: number; currency: string }) =>
  w.refund_due === 0 ? "nothing to refund"
    : w.refunded >= w.refund_due ? `refunded ${moneyOf(w.refunded, w.currency)}`
    : `refund of ${moneyOf(w.refund_due - w.refunded, w.currency)} pending, due by ${new Date(w.withdrawn_at + 14 * DAY_MS).toLocaleDateString()}`;

/**
 * Withdrawing within 14 days (Terms §7.6 and §8): each purchase still open to it, with the refund it would bring (the
 * unused share by turns, or by days for a plan, whichever is larger), a confirm step, and every withdrawal's status.
 */
function WithdrawSetting() {
  type R = Awaited<ReturnType<typeof api.purchases>>;
  const [data, setData] = useState<R | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const load = () => api.purchases().then(setData, () => setData({ purchases: [], withdrawals: [] }));
  useEffect(() => { void load(); }, []);
  const go = (id: string) => {
    setBusy(true);
    api.withdraw(id).then((r) => { setSaid(r.done ? `Done: ${moneyOf(r.refund, r.currency)} is on its way back to your card (usually 5 to 10 days). We've emailed you a confirmation.` : `Received. We'll refund ${moneyOf(r.refund, r.currency)} within 14 days, and we've emailed you a confirmation.`); setAsking(null); void load(); },
      () => setSaid("Couldn't withdraw just now. Try again, or write to hello@interfacedelta.com.")).finally(() => setBusy(false));
  };
  return (
    <div className="lm-mature">
      <p>Within 14 days of buying a plan, a yearly renewal or a pack, you can withdraw from it and get back the share you haven't used. In the EU and UK this is also your legal right of withdrawal.</p>
      {!data ? <p className="lm-gate-small">…</p> : (
        <>
          {!data.purchases.length ? <p className="lm-gate-small">Nothing to withdraw from right now.</p> : (
            <ul className="lm-withdraw">
              {data.purchases.map((p) => (
                <li key={p.id}>
                  <span>{purchaseName(p.item)} · {new Date(p.paid_at).toLocaleDateString()} · {moneyOf(p.amount, p.currency)}. You've used {p.used} of {p.turns.toLocaleString()} turns: refund {moneyOf(p.refund, p.currency)}.</span>
                  {asking === p.id ? (
                    <span className="lm-del-ask">
                      <span>{p.item in PLANS ? "This ends your Patronage now." : "This removes the pack's unused turns."}</span>
                      <button type="button" className="lm-del-yes" disabled={busy} onClick={() => go(p.id)}>{busy ? "Withdrawing…" : `Withdraw and refund ${moneyOf(p.refund, p.currency)}`}</button>
                      <button type="button" className="lm-del-no" disabled={busy} onClick={() => setAsking(null)}>keep it</button>
                    </span>
                  ) : <button type="button" onClick={() => setAsking(p.id)}>Withdraw from contract</button>}
                </li>
              ))}
            </ul>
          )}
          {data.withdrawals.length > 0 && (
            <>
              <p className="lm-gate-small">Your withdrawals</p>
              <ul className="lm-withdraw">
                {data.withdrawals.map((w) => <li key={w.id}><span>{purchaseName(w.item)} · withdrawn {new Date(w.withdrawn_at).toLocaleDateString()} · {refundStatus(w)}</span></li>)}
              </ul>
            </>
          )}
        </>
      )}
      {said && <p role="status">{said}</p>}
    </div>
  );
}

/** The narrator voice for new stories: picked from each story's opening, or one the player chose. */
function VoiceSetting() {
  const [voice, setVoice] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { if (ageOk()) api.settings().then((r) => setVoice(r.voice), () => setVoice("auto")); else setVoice("auto"); }, []);
  const save = (v: string) => { setFailed(false); setVoice(v); api.setVoicePref(v).then((r) => setVoice(r.voice), () => setFailed(true)); };
  const options: [string, string, string][] = [
    ["auto", "Let Weftwake choose", "Each story gets the voice its opening suggests."],
    ...PLAYER_VOICES.map((id): [string, string, string] => [id, VOICE_NAMES[id], VOICE_BLURBS[id]]),
  ];
  return (
    <div className="lm-mature">
      <p>The voice the story is told in. A story keeps its voice; you can change it in any story's journal. This sets new stories.</p>
      <div className="lm-voices" role="radiogroup" aria-label="Narrator for new stories">
        {options.map(([id, name, blurb]) => (
          <button key={id} type="button" role="radio" aria-checked={voice === id} disabled={voice === null} onClick={() => save(id)}>
            <strong>{name}</strong><span>{blurb}</span>
          </button>
        ))}
      </div>
      {failed && <p role="alert">Couldn't save that. Check your connection and try again.</p>}
      <VoiceFollows />
    </div>
  );
}

/** Let a long story's voice turn at a new chapter (worker/voice-turn.ts); on unless the player turns it off. */
function VoiceFollows() {
  const [on, setOn] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { if (ageOk()) api.settings().then((r) => setOn(r.voiceFollows), () => setOn(true)); else setOn(true); }, []);
  const save = (v: boolean) => { setFailed(false); api.setVoiceFollows(v).then((r) => setOn(r.voiceFollows), () => setFailed(true)); };
  return (
    <>
      <p><strong>Let the voice follow the story.</strong> A long story's telling may change at a new chapter, as you or the story change. Choosing a voice yourself keeps it.</p>
      <div className="lm-privacy-actions">
        <button type="button" disabled={on === null} onClick={() => save(!on)}>{on ? "Keep each story's voice" : "Let the voice follow the story"}</button>
        <span className="lm-gate-small lm-state" data-on={on ? "1" : "0"}>{on === null ? "…" : on ? "Follows the story" : "Kept"}</span>
      </div>
      {failed && <p role="alert">Couldn't save that. Check your connection and try again.</p>}
    </>
  );
}

/** How plates draw the player (Settings.drawAs): from the story by default, or as they choose. */
function DrawAsSetting() {
  const [drawAs, setDrawAs] = useState<DrawAsChoice | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { if (ageOk()) api.settings().then((r) => setDrawAs(r.drawAs), () => setDrawAs("story")); else setDrawAs("story"); }, []);
  const save = (v: DrawAsChoice) => { setFailed(false); setDrawAs(v); api.setDrawAs(v).then((r) => setDrawAs(r.drawAs), () => setFailed(true)); };
  const options: [DrawAsChoice, string][] = [["story", "From the story"], ["man", "A man"], ["woman", "A woman"]];
  return (
    <div className="lm-mature">
      <p>How the pictures of your chapters draw you. From the story: a woman if your story shows you are one, otherwise a man. Applies to new plates.</p>
      <div className="lm-pace" role="radiogroup" aria-label="Draw me as">
        {options.map(([id, label]) => (
          <button key={id} type="button" role="radio" aria-checked={drawAs === id} disabled={drawAs === null} onClick={() => save(id)}>{label}</button>
        ))}
      </div>
      {failed && <p role="alert">Couldn't save that. Check your connection and try again.</p>}
    </div>
  );
}

/** How fast the narrator writes on screen (this device). Holding a reply still hurries it. */
function PaceSetting() {
  const [cps, setCps] = useState(readPace);
  const chosen = PACES.reduce((a, b) => (Math.abs(b.cps - cps) < Math.abs(a.cps - cps) ? b : a));
  return (
    <div className="lm-mature">
      <p>How fast the story writes itself onto the page. Holding the words still speeds up a single reply.</p>
      <div className="lm-pace" role="radiogroup" aria-label="Reading speed">
        {PACES.map((p) => (
          <button key={p.label} type="button" role="radio" aria-checked={p === chosen} onClick={() => { savePace(p.cps); setCps(p.cps); }}>{p.label}</button>
        ))}
      </div>
      <p className="lm-gate-small">Kept on this device.</p>
    </div>
  );
}

// ---- sheets ----------------------------------------------------------------------------------------

export function Sheet({ label, onClose, children }: { label: string; onClose(): void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    // Focus starts on the sheet itself (its content may still be loading; "Close" is never the first stop), and Tab
    // stays inside it while it's open.
    const first = ref.current?.querySelector<HTMLElement>("textarea, input");
    (first ?? ref.current)?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab" || !ref.current) return;
      const items = [...ref.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex='-1'])")];
      if (!items.length) { e.preventDefault(); return; }
      const i = items.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); items[items.length - 1]!.focus(); }
      else if (!e.shiftKey && (i === -1 || i === items.length - 1)) { e.preventDefault(); items[0]!.focus(); }
    };
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("keydown", key); prev?.focus?.(); };
  }, [onClose]);
  return (
    <div className="lm-sheet-back" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lm-sheet" role="dialog" aria-modal="true" aria-label={label} ref={ref} tabIndex={-1}>{children}</div>
    </div>
  );
}

export function FeedbackSheet({ page, send, onClose }: {
  page: string; send(text: string, page: string, replyTo?: string): Promise<{ ok: boolean; resources?: Resource[] }>; onClose(): void;
}) {
  const [text, setText] = useState("");
  // Empty unless the player types an address: never filled in from their account, so a note stays anonymous by default.
  const [replyTo, setReplyTo] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed" | "bad_email">("idle");
  const [resources, setResources] = useState<Resource[] | null>(null);
  const submit = async () => {
    if (!text.trim()) return;
    setState("sending");
    try {
      const r = await send(text.trim(), page, replyTo.trim() || undefined);
      if (r.resources) setResources(r.resources);
      setState("sent");
    } catch (e) { setState(e instanceof ApiError && e.code === "bad_email" ? "bad_email" : "failed"); }
  };
  return (
    <Sheet label="Feedback" onClose={onClose}>
      {state === "sent" && !resources && (<><p>{replyTo.trim() ? "Thanks — read by a human, and we'll write back." : "Thanks — read by a human."}</p><button type="button" onClick={onClose}>Close</button></>)}
      {resources && (
        <>
          <p>That sounded like it might be about you. Your note wasn't stored. If you're going through something, you don't have to carry it alone:</p>
          <ul>{resources.map((r) => <li key={r.name}><a href={r.href}>{r.name}</a> {r.detail}</li>)}</ul>
          <button type="button" onClick={onClose}>Close</button>
        </>
      )}
      {state !== "sent" && (
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          {FROM_FEEDBACK.length > 0 && (
            <div className="lm-feedback-changed">
              <p>Recently changed because of what players told us:</p>
              <ul>{FROM_FEEDBACK.slice(0, 4).map((x) => <li key={x.date + x.text}><time dateTime={x.date}>{shortDate(x.date)}</time> {x.text}</li>)}</ul>
            </div>
          )}
          <label htmlFor="lm-feedback">Weftwake is young and changes often. Problems, praise, ideas, what you wish it did: tell us. Anonymous: your stories aren't attached.</label>
          <textarea id="lm-feedback" rows={5} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
          <label htmlFor="lm-feedback-reply" className="lm-feedback-reply">Email me if you'd like a reply <span>(optional; used only to answer this note)</span></label>
          <input id="lm-feedback-reply" type="email" inputMode="email" autoComplete="email" maxLength={254} placeholder="you@example.com"
            value={replyTo} onChange={(e) => { setReplyTo(e.target.value); if (state === "bad_email") setState("idle"); }} />
          {state === "bad_email" && <p role="alert">That email address doesn't look right. Fix it, or leave the box empty to stay anonymous.</p>}
          {state === "failed" && <p role="alert">Couldn't send that. Check your connection and try again.</p>}
          <div className="lm-sheet-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={!text.trim() || state === "sending"}>{state === "sending" ? "Sending…" : "Send"}</button>
          </div>
        </form>
      )}
    </Sheet>
  );
}

/** "2026-10-30" → "Oct 30" (the year only when it isn't this one). */
const shortDate = (iso: string) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }) });
};

export function DeleteSheet({ forget, onDone, onClose }: { forget(): Promise<unknown>; onDone(): void; onClose(): void }) {
  const [state, setState] = useState<"idle" | "deleting" | "failed">("idle");
  return (
    <Sheet label="Delete everything" onClose={onClose}>
      <p>Delete all your Weftwake stories, plates, images and "look closer" texts? This can't be undone. If you're a Patron, your Patronage is cancelled too, and unused pack turns are lost.</p>
      {state === "failed" && <p role="alert">Couldn't delete (or couldn't cancel your Patronage first), so nothing was deleted. Check your connection and try again.</p>}
      <div className="lm-sheet-actions">
        <button type="button" onClick={onClose}>Keep everything</button>
        <button type="button" className="lm-danger" disabled={state === "deleting"}
          onClick={() => { setState("deleting"); forget().then(onDone, () => setState("failed")); }}>
          {state === "deleting" ? "Deleting…" : "Delete everything"}
        </button>
      </div>
    </Sheet>
  );
}

export function Deleted({ onAgain }: { onAgain(): void }) {
  return (
    <main className="lm-page lm-deleted">
      <h1>Everything is deleted.</h1>
      <p>Your stories, plates and images are gone from Weftwake. If you come back, you start fresh.</p>
      <button type="button" className="lm-cta" onClick={onAgain}>Start a new story</button>
    </main>
  );
}

/** Chapters for new stories: on (a dramatic question each chapter, a moment of decision, an ending) or free flow. */
function ChaptersSwitch() {
  const [on, setOn] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { if (ageOk()) api.settings().then((r) => setOn(r.chapters), () => setOn(true)); else setOn(true); }, []);
  const save = (v: boolean) => { setFailed(false); api.setChapters(v).then((r) => setOn(r.chapters), () => setFailed(true)); };
  return (
    <div className="lm-mature">
      <p>With chapters, each part of a story asks a question and builds to a moment where you decide it, then the chapter ends. Turn them off and stories simply flow: tension still rises and falls, but nothing asks to be resolved. This sets new stories; any story can switch in its journal.</p>
      <div className="lm-privacy-actions">
        <button type="button" disabled={on === null} onClick={() => save(!on)}>{on ? "Start new stories without chapters" : "Start new stories with chapters"}</button>
        <span className="lm-gate-small lm-state" data-on={on ? "1" : "0"}>{on === null ? "…" : on ? "Chapters on" : "Free flow"}</span>
      </div>
      {failed && <p role="alert">Couldn't save that. Check your connection and try again.</p>}
    </div>
  );
}

/**
 * Mature themes (18+): romance, desire and intimacy may be part of the story, written with literary restraint (never
 * explicit). Off by default; turning it on takes an "I'm 18 or older" confirmation. Loaded only after the age gate,
 * so the privacy page alone stores nothing.
 */
function MatureSwitch() {
  const [mature, setMature] = useState<boolean | null>(null);
  const [asking, setAsking] = useState(false);
  const [adult, setAdult] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => { if (ageOk()) api.settings().then((r) => setMature(r.mature), () => setMature(false)); else setMature(false); }, []);
  const save = (on: boolean) => {
    setFailed(false);
    api.setMature(on, on && adult).then((r) => { setMature(r.mature); setAsking(false); }, () => setFailed(true));
  };
  return (
    <div className="lm-mature">
      <p>With mature themes on, romance, desire and intimacy can be part of your stories, written the way literary fiction writes them: sensual, never explicit (the story cuts away before a scene would become explicit). Off, romance stays to longing and a kiss. Violence follows your lead either way. Nothing involving anyone underage, nothing without consent, ever.</p>
      <div className="lm-privacy-actions">
        {mature
          ? <button type="button" onClick={() => save(false)}>Turn mature themes off</button>
          : <button type="button" onClick={() => setAsking(true)} disabled={mature === null}>Turn mature themes on…</button>}
        <span className="lm-gate-small lm-state" data-on={mature ? "1" : "0"}>{mature === null ? "…" : mature ? "On" : "Off"}</span>
      </div>
      {failed && <p role="alert">Couldn't save that. Check your connection and try again.</p>}
      {asking && (
        <Sheet label="Mature themes" onClose={() => setAsking(false)}>
          <p>Mature themes are for adults only.</p>
          <label className="lm-check">
            <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} /> I'm 18 or older
          </label>
          <div className="lm-sheet-actions">
            <button type="button" onClick={() => setAsking(false)}>Cancel</button>
            <button type="button" disabled={!adult} onClick={() => save(true)}>Turn on</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
